import Dexie, { Table } from 'dexie';
import { Recipe } from '../types/recipe';
import { Category } from '../types/category';

export class RecipeDatabase extends Dexie {
  recipes!: Table<Recipe>;
  categories!: Table<Category>;

  constructor() {
    super('RecipeDatabase');
    
    // Increment version to handle schema changes
    this.version(21).stores({
      recipes: '++id, title, category, difficulty, createdAt, updatedAt',
      categories: '++id, name, order, createdAt, updatedAt'
    });

    // Add hooks for automatic timestamps
    this.recipes.hook('creating', (primKey, obj) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
    });

    this.recipes.hook('updating', (modifications) => {
      modifications.updatedAt = new Date();
    });

    this.categories.hook('creating', (primKey, obj) => {
      obj.createdAt = new Date();
      obj.updatedAt = new Date();
      if (typeof obj.order !== 'number') {
        // Set order to the highest existing order + 1
        return db.categories.orderBy('order').last().then(lastCategory => {
          obj.order = (lastCategory?.order ?? -1) + 1;
          return obj;
        });
      }
      return obj;
    });

    this.categories.hook('updating', (modifications) => {
      modifications.updatedAt = new Date();
    });
  }
}

export const db = new RecipeDatabase();

// Helper function for safe database operations with retries
export async function safeDbOperation<T>(
  operation: () => Promise<T>,
  retries = 3
): Promise<T> {
  let lastError: Error | null = null;
  console.log('Starting database operation', {
    timestamp: new Date().toISOString(),
    retries
  });

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(`Database operation attempt ${attempt}/${retries}`);
      const result = await operation();
      console.log('Database operation successful', {
        attempt,
        timestamp: new Date().toISOString()
      });
      return result;
    } catch (error) {
      console.error('Database operation failed:', {
        attempt,
        maxRetries: retries,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
        stack: error instanceof Error ? error.stack : undefined
      });

      lastError = error instanceof Error ? error : new Error('Unknown database error');

      if (attempt === retries) {
        console.error('Max retries reached, operation failed', {
          timestamp: new Date().toISOString()
        });
        throw lastError;
      }

      // Wait before retrying (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
    }
  }

  throw lastError || new Error('Database operation failed');
}

// Initialize database with sample data
export const initializeDb = async (
  sampleData?: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>[]
) => {
  return safeDbOperation(async () => {
    const recipeCount = await db.recipes.count();
    const categoryCount = await db.categories.count();
    
    if (recipeCount === 0 && sampleData) {
      console.log('Initializing database with sample recipes...');
      
      // Bulk add sample recipes
      await db.recipes.bulkAdd(
        sampleData.map(recipe => ({
          ...recipe,
          id: crypto.randomUUID(),
          createdAt: new Date(),
          updatedAt: new Date(),
        }))
      );
      
      console.log(`Database initialized with ${sampleData.length} recipes`);
    } else {
      console.log(`Database already contains ${recipeCount} recipes`);
    }

    // Initialize default categories if none exist
    if (categoryCount === 0) {
      console.log('Initializing default categories...');
      
      // Add default categories in bulk
      const defaultCategories = [
        'Smoothies',
        'Fruit Dishes',
        'Salads',
        'Teas',
        'Platters',
        'Warm Dishes'
      ].map((name, index) => ({
        id: crypto.randomUUID(),
        name,
        order: index,
        createdAt: new Date(),
        updatedAt: new Date(),
      }));
      
      await db.categories.bulkAdd(defaultCategories);
      
      console.log(`Initialized ${defaultCategories.length} default categories`);
    }
    
    // Ensure all categories have an order
    const categoriesWithoutOrder = await db.categories
      .filter(cat => typeof cat.order !== 'number')
      .toArray();

    if (categoriesWithoutOrder.length > 0) {
      console.log('Fixing categories without order...');
      const maxOrder = await db.categories
        .filter(cat => typeof cat.order === 'number')
        .toArray()
        .then(cats => Math.max(...cats.map(c => c.order), -1));
      
      for (let i = 0; i < categoriesWithoutOrder.length; i++) {
        const category = categoriesWithoutOrder[i];
        await db.categories.update(category.id, {
          order: maxOrder + i
        });
      }
      console.log(`Fixed ${categoriesWithoutOrder.length} categories without order`);
    }
  });
};